import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = { chatroomId: Number }

  static targets = ["firstName", "lastName", "dni", "cuit", "birthDate", "email", "errors"]

  // Patterns to detect landing form messages
  static LANDING_FORM_PATTERNS = [
    /- Nombre:\s*(.+)/i,
    /- DNI:\s*(\d+)/i,
    /- Fecha de nacimiento:\s*(.+)/i,
    /- Email:\s*(.+)/i
  ]

  connect() {
    this.detectLandingFormMessages()
  }

  detectLandingFormMessages() {
    const container = document.getElementById(`messages_${this.chatroomIdValue}`)
    if (!container) return

    const messages = container.querySelectorAll('.message-bubble.inbound .message-content div[style*="pre-wrap"]')
    messages.forEach(messageDiv => {
      const content = messageDiv.textContent
      if (this.isLandingFormMessage(content)) {
        const messageBubble = messageDiv.closest('.message-bubble')
        if (messageBubble && !messageBubble.dataset.landingFormDetected) {
          messageBubble.dataset.landingFormDetected = 'true'
          messageBubble.style.cursor = 'pointer'
          messageBubble.title = 'Click para crear paciente con estos datos'

          // Add a subtle indicator
          const badge = document.createElement('div')
          badge.className = 'mt-2'
          badge.innerHTML = '<span class="badge badge-success" style="font-size: 0.75em;"><i class="fas fa-user-plus mr-1"></i>Click para crear paciente</span>'
          messageDiv.parentElement.appendChild(badge)

          messageBubble.addEventListener('click', (e) => {
            e.preventDefault()
            this.openCreatePatientModal(content)
          })
        }
      }
    })
  }

  isLandingFormMessage(content) {
    // Must have at least Nombre and DNI
    return /- Nombre:\s*.+/i.test(content) && /- DNI:\s*\d+/i.test(content)
  }

  parseFormData(content) {
    const data = {}

    const nombreMatch = content.match(/- Nombre:\s*(.+)/i)
    if (nombreMatch) {
      const fullName = nombreMatch[1].trim()
      const parts = fullName.split(/\s+/)
      // Last word is last name, rest is first name
      if (parts.length > 1) {
        data.lastName = parts.pop()
        data.firstName = parts.join(' ')
      } else {
        data.firstName = fullName
        data.lastName = ''
      }
    }

    const dniMatch = content.match(/- DNI:\s*(\d+)/i)
    if (dniMatch) data.dni = dniMatch[1].trim()

    const birthMatch = content.match(/- Fecha de nacimiento:\s*(.+)/i)
    if (birthMatch) data.birthDate = birthMatch[1].trim()

    const emailMatch = content.match(/- Email:\s*(.+)/i)
    if (emailMatch) data.email = emailMatch[1].trim()

    const referredMatch = content.match(/- Recomendado por:\s*(.+)/i)
    if (referredMatch) data.referredBy = referredMatch[1].trim()

    return data
  }

  openCreatePatientModal(content) {
    const data = this.parseFormData(content)
    const modal = document.getElementById(`createPatientFromMessageModal_${this.chatroomIdValue}`)
    if (!modal) return

    // Fill form fields
    if (this.hasFirstNameTarget) this.firstNameTarget.value = data.firstName || ''
    if (this.hasLastNameTarget) this.lastNameTarget.value = data.lastName || ''
    if (this.hasDniTarget) this.dniTarget.value = data.dni || ''
    if (this.hasCuitTarget) this.cuitTarget.value = data.cuit || ''
    if (this.hasBirthDateTarget) this.birthDateTarget.value = data.birthDate || ''
    if (this.hasEmailTarget) this.emailTarget.value = data.email || ''

    // Show referred by info if present
    const referredByContainer = modal.querySelector('.referred-by-info')
    if (referredByContainer && data.referredBy) {
      referredByContainer.textContent = `Recomendado por: ${data.referredBy}`
      referredByContainer.style.display = 'block'
    }

    // Clear previous errors
    if (this.hasErrorsTarget) this.errorsTarget.innerHTML = ''

    // Show modal
    $(modal).modal('show')
  }

  async submitCreatePatient(event) {
    event.preventDefault()

    const chatroomId = this.chatroomIdValue
    const submitBtn = event.currentTarget
    submitBtn.disabled = true
    submitBtn.innerHTML = '<span class="spinner-border spinner-border-sm mr-1"></span>Creando...'

    const body = {
      first_name: this.firstNameTarget.value,
      last_name: this.lastNameTarget.value,
      dni: this.dniTarget.value,
      cuit: this.cuitTarget.value,
      birth_date: this.birthDateTarget.value,
      email: this.emailTarget.value
    }

    try {
      const response = await fetch(`/chatrooms/${chatroomId}/create_patient_from_message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
          'Accept': 'text/vnd.turbo-stream.html'
        },
        body: JSON.stringify(body)
      })

      const html = await response.text()

      if (response.ok) {
        Turbo.renderStreamMessage(html)
        const modal = document.getElementById(`createPatientFromMessageModal_${chatroomId}`)
        $(modal).modal('hide')

        // Remove the "click to create" badges
        const container = document.getElementById(`messages_${chatroomId}`)
        if (container) {
          container.querySelectorAll('[data-landing-form-detected]').forEach(el => {
            el.style.cursor = ''
            el.title = ''
            const badge = el.querySelector('.badge-success')
            if (badge) badge.closest('div.mt-2').remove()
          })
        }
      } else {
        // Show errors in modal
        Turbo.renderStreamMessage(html)
      }
    } catch (error) {
      if (this.hasErrorsTarget) {
        this.errorsTarget.innerHTML = `<div class="alert alert-danger">Error de conexion. Intenta nuevamente.</div>`
      }
    } finally {
      submitBtn.disabled = false
      submitBtn.innerHTML = '<i class="fas fa-user-plus mr-1"></i>Crear Paciente'
    }
  }
}
